import './NotificationItem.scss';

import { NotificationData, NotificationsDispatch } from '../notifications-types';
import React, { useMemo } from 'react';
import FadingCircleLoader from '../../components/FadingCircleLoader';
import moment from 'moment';
import { BlueDotIcon } from '../../icons/icons';
import { useDispatch } from 'react-redux';
import { markAsRead, markAsUnread, markForDelete } from '../actions';
import GfActionMenu from '../../gfcomponents/GfActionMenu/GfActionMenu';
import { useUserPersistedState } from '../../persistedstate/users/useUserPersistedState';
import classNames from 'classnames';
import NotificationIcon from './NotificationIcon';
import NotificationText from './NotificationText';
import NotificationLink from './NotificationLink';

export type NotificationItemProps = {
  notificationData: NotificationData;

  /**
   * Callback function to handle the opening and closing of the action menu. This function used for the <ActionMenu /> component to control open and close state of the <Popup> component.
   */
  handleActionMenuOpen: (isOpen: boolean) => VoidFunc;

  handleNotificationCenterOpen: (isOpen: boolean) => VoidFunc;

  handleNotificationDeleteModal: (isOpen: boolean, notificationData: NotificationData[]) => VoidFunc;

  className?: string; // this is for the Semantic Transition component

  style?: React.CSSProperties; // this is for the Semantic Transition component
};

export default function NotificationItem(props: NotificationItemProps) {
  const dispatch = useDispatch<NotificationsDispatch>();
  const [dismissedDeleteNotificationModal, _] = useUserPersistedState('dismissDeleteNotificationPrompt', false);
  const { notificationData, handleActionMenuOpen, handleNotificationCenterOpen, handleNotificationDeleteModal, className, style } = props;

  const actionMenuItems = useMemo(() => {
    const handleClearNotification = () => {
      if (!dismissedDeleteNotificationModal) {
        handleNotificationDeleteModal(true, [notificationData])();
      } else {
        markForDelete([notificationData], dispatch);
      }
    };
    const markAsReadItem = { name: 'Mark as read', icon: 'check', onClick: () => markAsRead([notificationData], dispatch) };
    const markAsUnreadItem = { name: 'Mark as unread', icon: 'bell outline', onClick: () => markAsUnread([notificationData], dispatch) };
    const clearNotificationItem = { name: 'Clear notification', icon: 'bell slash outline', onClick: handleClearNotification };

    if (notificationData.read) {
      return [markAsUnreadItem, clearNotificationItem];
    } else {
      return [markAsReadItem, clearNotificationItem];
    }
  }, [notificationData, dismissedDeleteNotificationModal, handleNotificationDeleteModal, dispatch]);

  return (
    <div className={classNames('notification-item', className ?? '')} style={style}>
      <div className="unread-dot">{!notificationData.read && <BlueDotIcon />}</div>
      <NotificationIcon notificationData={props.notificationData} />
      <div className="notification-content">
        {notificationData.text && (
          <span className="notification-text">
            <NotificationText notificationData={notificationData} />
          </span>
        )}
        <NotificationLink notificationData={notificationData} handleNotificationCenterOpen={handleNotificationCenterOpen} />
        {notificationData.smallText ? <span className="small-text">{notificationData.smallText}</span> : null}
        <span className="timestamp">{moment(notificationData.timestamp).fromNow()}</span>
      </div>
      <div className="ellipses-button">
        {!notificationData.completed ? (
          <FadingCircleLoader />
        ) : (
          <GfActionMenu
            className="notification-item"
            menuClassName="notifications-item-menu"
            vertical={true}
            items={actionMenuItems}
            onActionMenuOpen={handleActionMenuOpen(true)}
            onActionMenuClose={handleActionMenuOpen(false)}
          />
        )}
      </div>
    </div>
  );
}
